import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AcceptInvitationStepBase } from '../../shared/accept-invitation-step-base/accept-invitation-step-base';
import businessStructureOptions from '../../shared/data/business-structure-options.json';
import { OwnerStateService } from '../../services/owner-state.service';
import { Subscription } from 'rxjs';
import { CurrencyPipe } from '@angular/common';
import { InvitationCommunicationService } from '../../services/invitation-communication.service';
import {
  clearMaskedSsnOnKeydown,
  clearMaskedSsnOnPaste,
  ssnReplacePattern,
  ssnValidator,
} from '../../utils/ssn';

@Component({
  selector: 'app-business-structure',
  templateUrl: './business-structure.component.html',
  styleUrl: './business-structure.component.scss',
  providers: [CurrencyPipe]
})
export class BusinessStructureComponent extends AcceptInvitationStepBase<any> implements OnInit, OnDestroy {
  private readonly ownerStateService = inject(OwnerStateService);
  private readonly currencyPipe = inject(CurrencyPipe);
  private readonly commService = inject(InvitationCommunicationService);
  private subscription: Subscription | undefined;
  private structureSub: Subscription | undefined;
  private ssnSub: Subscription | undefined;

  public readonly ssnReplacePattern = ssnReplacePattern;
  public ssnMasked: boolean = true;
  public isSoleProp: boolean = false;
  private ssnBaseValidators = [ssnValidator];
  private businessTaxIdBaseValidators = [Validators.pattern(/^$|^\d{9}$/)];
  public role = this.commService.role;

  public override readonly form: FormGroup = inject(FormBuilder).group({
    businessStructure: ['', Validators.required],
    businessSsn: ['', this.ssnBaseValidators],
    businessTaxID: ['', this.businessTaxIdBaseValidators],
    industryType: ['', Validators.required],
    yearsInBusiness: ['', Validators.required],
    numberOfEmployees: ['', [Validators.required, Validators.min(1)]],
    grossAnnualRevenue: ['', [Validators.required, Validators.min(1), Validators.max(10 ** 16 - 1)]],
    referralNumber: ['', [Validators.maxLength(15)]]
  });
  grossAnnualRevenueDisplay = '';

  public override readonly nextUrl = '../business-location';
  public override readonly previousUrl = '../about-business';
  protected override readonly storageKey = 'businessStructureForm';

  // Options for the selectors
  public businessStructureOptions: any[] = [];
  public readonly industryOptions = (businessStructureOptions as any).industryOptions;
  public readonly yearsInBusinessOptions = (businessStructureOptions as any).yearsInBusinessOptions;

  protected override afterFormLoaded(): void {
    // Runs after the saved draft data has been loaded into the form.
    const val = this.form.get('grossAnnualRevenue')?.value;
    if (val) {
      this.grossAnnualRevenueDisplay = this.currencyPipe.transform(val, 'USD', 'symbol', '1.0-0')!;
    }

    // Get SSN from application owner state
    const ownerSSN = this.ownerStateService.getApplicationOwnerSSN();
    if (ownerSSN) {
      this.form.get('businessSsn')!.setValue(ownerSSN);
    }

    // Then subscribe to business structure options changes
    this.subscription = this.ownerStateService.businessStructureOptions$.subscribe(options => {
      this.businessStructureOptions = options;

      // If current selection is sole proprietorship and it's being removed, clear the selection
      const currentValue = this.form.get('businessStructure')?.value;
      if (currentValue === 'SOLE_PROPRIETORSHIP' && !options.some((opt: any) => opt.value === 'SOLE_PROPRIETORSHIP')) {
        // Clear the form control
        this.form.patchValue({ businessStructure: '' });

        // Force form validation update
        this.form.get('businessStructure')?.updateValueAndValidity();

        // Mark the form as touched to show validation errors
        this.form.get('businessStructure')?.markAsTouched();
      }
    });

    // Check initial business structure value and set validators accordingly
    const initialBusinessStructure = this.form.get('businessStructure')?.value;
    this.isSoleProp = initialBusinessStructure === 'SOLE_PROPRIETORSHIP';
    this.updateSolePropValidators(this.isSoleProp); 

    // Make ssn required when business structure is sole proprietorship
    this.structureSub = this.form.get('businessStructure')!.valueChanges.subscribe(value => {
      this.isSoleProp = value === 'SOLE_PROPRIETORSHIP';
      this.updateSolePropValidators(this.isSoleProp);
    });

    // Update application owner ssn on changes to ssn form
    this.ssnSub = this.form.get('businessSsn')!.valueChanges.subscribe(value => {
      this.ownerStateService.setApplicationOwnerSSN(value);
    });
  }

  public ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.structureSub) {
      this.structureSub.unsubscribe();
    }
    if (this.ssnSub) {
      this.ssnSub.unsubscribe();
    }
  }

  public onlyNumberKey(event: KeyboardEvent): boolean {
    const charCode = event.which ?? event.keyCode;
    // block minus (45)
    if (charCode === 45) {
      return false;
    }
    // allow digits and control keys
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;
  }


  onRevenueInput(event: Event) {
    const input = event.target as HTMLInputElement;
    // allow digits and at most one "."
    let raw = input.value;
    // strip everything except digits and dots
    raw = raw.replace(/[^0-9.]/g, '');
    // if more than one dot, keep only the first
    const parts = raw.split('.');
    const numeric = parts.shift()! + (parts.length ? '.' + parts.join('') : '');
    this.form.get('grossAnnualRevenue')!.setValue(numeric, { emitEvent: true });
    this.grossAnnualRevenueDisplay = numeric;
  }

  onRevenueBlur() {
    const val = parseFloat(this.form.get('grossAnnualRevenue')!.value) || 0;
    this.grossAnnualRevenueDisplay =
      this.currencyPipe.transform(val, 'USD', 'symbol', '1.2-2')!;
  }

  /**
   * Update ssn validators
   */
  private updateSolePropValidators(isSoleProp: boolean) {
    const ssnControl = this.form.get('businessSsn')!;
    const taxIdControl = this.form.get('businessTaxID')!;

    if (isSoleProp) {
      ssnControl.setValidators([Validators.required, ...this.ssnBaseValidators]);
      taxIdControl.setValidators(this.businessTaxIdBaseValidators);
    } else {
      ssnControl.setValidators(this.ssnBaseValidators);
      taxIdControl.setValidators([Validators.required, ...this.businessTaxIdBaseValidators]);
    }

    ssnControl.updateValueAndValidity();
    taxIdControl.updateValueAndValidity();
  }

  /**
   * Toggles the visibility of the SSN field
   * @param event 
   */
  public toggleSSNVisibility(event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.ssnMasked = !this.ssnMasked;
  }

  public onSsnKeyDown(event: KeyboardEvent): void {
    clearMaskedSsnOnKeydown(this.form.get('businessSsn'), event);
  }

  public onSsnPaste(event: ClipboardEvent): void {
    clearMaskedSsnOnPaste(this.form.get('businessSsn'), event);
  }
}
